/* ★ v29.35（用户定案）：`dbViewConfig.vue` 按步骤拆分的「数据字典」集中地
   （与 `dbView/searchComponentSetting/constants.ts` 同一约定：常量留这里，逻辑留组件）。
   注意：`COL_TABLE_WIDTH` 由 `COL_COLUMNS` 求和得出，两者必须同文件，改列宽才不会漂。 */

/* ═══ 展示配置（Step 3）═══ */

export const VIEW_TYPE_OPTIONS = [
  { value: 'normal', label: '标准视图' },
  { value: 'pivot', label: '透视表' },
  { value: 'pivot_charts', label: '透视图' },
];

/* ═══ 列配置（Step 4）═══ */

/* cell_component 可选项（对照 liyu-pc-lib/src/components/cellComponent/ 实存组件，
   挑通用项；金额等特殊语义可后续按需扩充）。
   ★ v29.38（用户定案）：label 一律不带原始英文（原来写作「中文(datetime)」）——
   `value` 本身就是要落的英文枚举值，界面不再重复展示；若日后要按英文搜下拉，
   给该 `a-select` 加 `:filter-option="(v, o) => String(o.value).includes(v) || String(o.label).includes(v)"`。 */
export const CELL_COMPONENT_OPTIONS = [
  { value: 'datetime', label: '日期时间' },
  { value: 'datetime_hh_mm', label: '日期时间到分' },
  { value: 'time', label: '时间' },
  { value: 'date', label: '日期' },
  { value: 'date_year', label: '年份' },
  { value: 'month', label: '月份' },
  { value: 'boolean', label: '布尔' },
  { value: 'tag', label: '标签' },
  { value: 'tag_list', label: '标签列表' },
  { value: 'json', label: 'JSON' },
  { value: 'json_tag', label: 'JSON标签' },
  { value: 'money', label: '金额' },
  { value: 'to_fixed_0', label: '整数' },
  { value: 'to_fixed_2', label: '两位小数' },
  { value: 'to_fixed_4', label: '四位小数' },
  { value: 'percent', label: '百分比' },
  { value: 'tooltip', label: '悬浮提示' },
  { value: 'image', label: '图片' },
  { value: 'image_url', label: '图片链接' },
  { value: 'link', label: '链接' },
  { value: 'url_out', label: '外链' },
  { value: 'obj', label: '对象' },
  { value: 'list', label: '列表' },
  { value: 'color', label: '颜色块' },
  { value: 'bolder', label: '加粗' },
  { value: 'click_copy', label: '点击复制' },
];

/* 探测类型 → class_name / cell_component（对齐后端 sync_columns 的映射）
   ★ v29.23.1：后端 probe_columns 改真实行推断（_py_type_name），type 为
   int/float/bool/datetime/date/str 六类真实类型名，str 显式映射（probe 值优先于已有值）。 */
export const PROBE_CLASS_NAME_MAP: Record<string, string> = {
  int: 'int',
  float: 'float',
  bool: 'bool',
  datetime: 'datetime',
  date: 'date',
  str: 'str',
};
export const PROBE_CELL_MAP: Record<string, string> = {
  datetime: 'datetime',
  date: 'date',
  bool: 'tag',
  json: 'json',
};

/* s-table 行高：本表每格都是 small 尺寸输入控件（24px），36 留出上下呼吸空间 */
export const COL_ROW_HEIGHT = 36;

/* 列配置表列定义。
   antdv ColumnsType 对 fixed 等字段是字面量类型，这里用 any[] 放宽避免字面量拓宽报错。
   ★ v29.16（用户定案）：去掉列宽(width)配置；新增 可拖宽(resizable)/汇总(summary)/
   可排序(is_sortable)；content_map+color_map 合并为「转换/颜色」一列（模态框联合配置）；
   新增「搜索组件」列（模态框配置高级搜索组件对象）。 */
export const COL_COLUMNS: any[] = [
  { title: '顺序', key: 'column_order', width: 80, fixed: 'left' },
  {
    title: '列名',
    dataIndex: 'data_index',
    key: 'data_index',
    width: 140,
    fixed: 'left',
  },
  { title: '列展示名', key: 'title', width: 130, fixed: 'left' },
  // ★ v29.40：原「唯一主键」→「主键」（主键改为可多选，措辞不能再带「唯一」）；
  //   文案变短，列宽 85 → 70（COL_TABLE_WIDTH 由本数组求和派生，改宽自动同步）
  { title: '主键', key: 'is_primary_key', width: 70 },
  { title: '类型', dataIndex: 'class_name', key: 'class_name', width: 80 },

  { title: '查询名', key: 'query_title', width: 120 },
  { title: '单元格组件', key: 'cell_component', width: 150 },
  { title: '显示', key: 'checked', width: 55 },
  { title: '可拖宽', key: 'resizable', width: 65 },
  { title: '合计', key: 'summary', width: 55 },
  { title: '可排序', key: 'is_sortable', width: 65 },
  { title: '基础搜索', key: 'is_base_query', width: 75 },
  { title: '默认搜索', key: 'is_default_query', width: 75 },
  { title: '分组', key: 'is_group_query', width: 55 },
  { title: '默认排序', key: 'default_sort', width: 200 },
  { title: '转换/颜色', key: 'map_color', width: 90 },
  { title: '搜索组件', key: 'adv_search', width: 90 },
  { title: '操作', key: 'action', width: 60, fixed: 'right' },
];

/* ★ v29.34：s-table 的横向滚动宽度 = 表格总宽，直接由各列显式 width 求和得出
   （原来是手写的 `:scroll="{ x: 1685 }"`，改列宽就会漂；现在改 width 自动跟随）。 */
export const COL_TABLE_WIDTH = COL_COLUMNS.reduce((sum: number, c: any) => sum + (c.width || 0), 0);

/* ═══ 步骤导航（外壳用）═══ */

/* 前进/跳步时只校验「当前步骤」的必填项（其余步骤的错误不可见，全量校验会让用户困惑）。
   ★ v29.23：菜单四项拆出 → 4 步。
   ★ v29.45（用户定案）：「菜单与权限」挪到末位并改名「菜单」→ 现为
   基础配置 / 展示配置 / 列配置 / 菜单。
   ⚠️ 本数组是**按步骤位置**排的：改步骤顺序时，这里的顺序与上面每个注释的措辞必须一起改，
      否则会「校验了另一步的字段」还看不出错。 */
export const STEP_FIELDS: string[][] = [
  ['name', 'flag', 'data_source_id', 'sql'], // 第 1 步 基础配置
  [], // 第 2 步 展示配置（无必填项）
  [], // 第 3 步 列配置（随视图一起保存）
  [], // 第 4 步 菜单（无必填项：父级留空 = 顶级菜单；两项由 save_view 同步到菜单行）
];
